import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Paper
} from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { Actions } from "../../../core/modules/Actions";
import DocumentRepository from "../../../core/repository/DocumentRepository";
import TextButtonComponet from "../../atom/Buttons/TextButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PopUpMessageComponent from "../../molecule/PopupMessage/PopUpMessage";

const DocumentNote = ({ documentId, pageNumber }) => {
  const dispatch = useDispatch();
  const [noteText, setNoteText] = useState("");
  const [savedNoteText, setSavedNoteText] = useState("");
  const [isEditing, setIsEditing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const fetchPageNote = useCallback(async () => {
    if (!documentId || !pageNumber) return;
    setLoading(true);
    try {
      const response = await DocumentRepository.getPageNote({
        document_id: documentId,
        page_no: pageNumber,
      });
      const existingContent =
        response?.documentNote?.note || "";

      setNoteText(existingContent);
      setSavedNoteText(existingContent);
      setIsEditing(!existingContent);
    } catch (error) {
      console.error("Failed to fetch note:", error);
      setNoteText("");
      setSavedNoteText("");
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  }, [documentId, pageNumber]);

  useEffect(() => {
    fetchPageNote();
  }, [fetchPageNote]);

  const handleSaveNote = async () => {
    const trimmedNote = noteText.trim();

    if (!documentId) {
      console.error("Document ID is missing!");
      return;
    }

    if (!trimmedNote) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        document_id: documentId,
        page_no: pageNumber,
        note: trimmedNote,
      };

      await dispatch(Actions.document.savePageNote(payload));
      setSavedNoteText(trimmedNote);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save note:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenClearModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseClearModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleConfirmClearNote = async () => {
    if (!documentId) return;

    setIsDeleteModalOpen(false);
    setClearing(true);
    try {
      const payload = {
        document_id: documentId,
        page_no: pageNumber,
        note: "",
        is_deleted: true,
      };

      await dispatch(Actions.document.savePageNote(payload));
      setNoteText("");
      setSavedNoteText("");
      setIsEditing(true);
    } catch (error) {
      console.error("Failed to clear note:", error);
    } finally {
      setClearing(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const isNoteEmpty = !noteText.trim();

  return (
    <Paper
      elevation={0}
      sx={{
        py: 3,
        px: 2,
        height: "100%",
        minHeight: { xs: "auto", md: "400px" },
        boxSizing: "border-box",
        borderRadius: "16px",
        border: "1px solid #E5E5E5",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        flex: 1,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#1A1A1A",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <SummarizeIcon sx={{ color: "#9834F0", fontSize: 24 }} />
          My Notes
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: "#8C8C8C",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          PAGE {pageNumber}
        </Typography>
      </Box>

      {/* Main Content Area */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={30} sx={{ color: "#9834F0" }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            gap: 2,
          }}
        >
          {isEditing ? (
            <>
              <TextField
                multiline
                rows={20}
                placeholder="Write your notes for this page..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                inputProps={{ maxLength: 2000 }}
                sx={{
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  backgroundColor: "#FAFAFA",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": {
                    height: "100%",
                    alignItems: "flex-start",
                    borderRadius: "12px",
                    "& fieldset": { borderColor: "#E5E5E5" },
                    "&:hover fieldset": { borderColor: "#9834F0" },
                    "&.Mui-focused fieldset": { borderColor: "#9834F0" },
                  },
                  "& .MuiInputBase-input": {
                    height: "100% !important",
                    overflowY: "auto !important",
                  },
                }}
              />

              {/* Character Counter */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <Typography variant="caption" sx={{ color: "#8C8C8C" }}>
                  {noteText.length}/2000
                </Typography>
              </Box>

              {/* Save Button */}
              <Box sx={{ flexShrink: 0 }}>
                <TextButtonComponet
                  text={
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                      <SaveIcon style={{ fontSize: 18 }} /> Save Note
                    </span>
                  }
                  onButtonClick={handleSaveNote}
                  loading={saving}
                  isDisabled={saving || isNoteEmpty}
                  style={{
                    backgroundColor: isNoteEmpty ? "#CCCCCC" : "#9834F0",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    borderRadius: "8px",
                    padding: "10px 16px",
                    border: "none",
                    cursor: isNoteEmpty || saving ? "not-allowed" : "pointer",
                    width: "100%",
                    transition: "background-color 0.2s ease-in-out",
                  }}
                  classStyle="btn"
                />
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  backgroundColor: "#FAFAFA",
                  borderRadius: "12px",
                  p: 2.5,
                  border: "1px solid #F0F0F0",
                  overflowY: "auto",
                  wordBreak: "break-word",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#D1D5DB",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
                }}
              >
                <Typography
                  sx={{
                    color: "#4A5568",
                    fontSize: "15px",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {savedNoteText}
                </Typography>
              </Box>

              {/* Edit Note Button */}
              <Box sx={{ display: "flex", gap: 1.5, width: "100%", flexShrink: 0 }}>
                <Box sx={{ flex: 1 }}>
                <TextButtonComponet
                  text={
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                      <EditIcon style={{ fontSize: 18 }} /> Edit
                    </span>
                  }
                  onButtonClick={handleEditClick}
                  style={{
                    backgroundColor: "#FFFFFF",
                    color: "#4B5563",
                    fontWeight: 600,
                    borderRadius: "12px",
                    padding: "12px 12px",
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                    width: "100%",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.2s ease-in-out",
                  }}
                  classStyle="btn edit-note-btn"
                />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextButtonComponet
                    text={
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                        <DeleteOutlineIcon style={{ fontSize: 18 }} /> Clear
                      </span>
                    }
                    onButtonClick={handleOpenClearModal}
                    loading={clearing}
                    isDisabled={clearing}
                    style={{
                      backgroundColor: "#FFFFFF",
                      color: "#EF4444",
                      fontWeight: 600,
                      borderRadius: "12px",
                      padding: "12px 12px",
                      border: "1px solid #FCA5A5",
                      cursor: clearing ? "not-allowed" : "pointer",
                      width: "100%",
                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s ease-in-out",
                    }}
                    classStyle="btn clear-note-btn"
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Confirmation Modal */}
      <PopUpMessageComponent
        open={isDeleteModalOpen}
        type="other"
        title="Delete Notes"
        message="Are you sure to delete notes ?"
        btntext="Delete"
        onclick={handleConfirmClearNote}
        altbtntext="Cancel"
        altonclick={handleCloseClearModal}
        onclose={handleCloseClearModal}
      />

      {/* Style overrides */}
      <style>{`
        .edit-note-btn:hover {
          background-color: #F9FAFB !important;
          color: #9834F0 !important;
          border-color: #D8B4FE !important;
        }
        .clear-note-btn:hover {
          background-color: #FEF2F2 !important;
          color: #DC2626 !important;
          border-color: #F87171 !important;
        }
      `}</style>
    </Paper>
  );
};

export default DocumentNote;